import {
  type DomainIntention,
  type DomainPriority,
  FITNESS_DOMAIN_LABELS,
  type FitnessDomain,
  type PlannedItem,
  type WeeklyCommitment,
} from '~/models/fitnessDomain'
import { weekAround } from './weekShape'

/**
 * The starting week Project Sola writes at setup (D-101).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS PLANS. IT DOES NOT JUDGE, AND THE DIFFERENCE IS THE WHOLE RECORD
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  `DOMAIN_REGISTRY` still reports cardio, strength and mobility as
 *  `implemented: false`, and nothing here changes that. A plan says *here is a
 *  week you told me you could do*; an opinion says *here is how that week went*.
 *  The second needs approved thresholds and collected evidence and is what D-060
 *  governs. **Adding a rule here that reads back the week is the error D-101 §2
 *  exists to forbid** — it belongs in a ruleset, behind a registry flag.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  EVERY NUMBER THIS FILE EMITS CAME FROM THE USER
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  That is D-101 §3 and it is the reason **`rules/thresholds.ts` gains nothing
 *  for this file.** The split is an equal share with the remainder going in the
 *  user's own priority order, which is integer division rather than a ratio
 *  anybody chose. `minutes` is the figure they typed, carried through unchanged
 *  and omitted entirely when they left it blank.
 *
 *  **A default duration, a recommended session count or a percentage reduction
 *  would each be a number from nowhere**, would need a threshold with provenance,
 *  and would put coaching in Project Sola's mouth that it never did. If a change
 *  here seems to need one, it needs a decision record first.
 */

/** What the wizard collected, reduced to the parts a plan can be built from. */
export interface StarterCommitmentInput {
  readonly domains: readonly FitnessDomain[]
  readonly intentions: Readonly<Partial<Record<FitnessDomain, DomainIntention>>>
  readonly priorities: Readonly<Partial<Record<FitnessDomain, DomainPriority>>>
  /** Sessions a week the user said they realistically train. */
  readonly sessionsPerWeek: number
  /** Their typical session length, if they gave one. */
  readonly minutesPerSession?: number | undefined
}

/**
 * The domains a training-session budget can honestly be spent on, in the order
 * ties are broken.
 *
 * **Body composition is absent because it is not trained in sessions** — its plan
 * is the phase and its weekly evidence is the derived checklist (D-099). **Mobility
 * is absent because nothing is collected to derive one from**: `needsTrainingContext`
 * never asks a mobility-only user for a session budget, so there is no answer of
 * theirs to restate. Both exclusions are D-101 §4, and neither is a scope cut that
 * can be closed by adding a number here.
 */
const SESSION_DOMAINS: readonly FitnessDomain[] = ['strength', 'cardio']

/**
 * How much of the remainder a domain gets first. **The user set this**, on the
 * intentions step, which is what makes the ordering theirs rather than ours.
 */
const PRIORITY_ORDER: Readonly<Record<DomainPriority, number>> = {
  primary: 0,
  supporting: 1,
  protect: 2,
  none: 3,
}

/** Planned against, as opposed to merely observed. Mirrors `isPlanned`. */
function isPlannedIntention(intention: DomainIntention | undefined): boolean {
  return intention === 'improve' || intention === 'maintain'
}

/**
 * One starting commitment per domain that has one, keyed by domain.
 *
 * **A commitment belongs to a domain goal, not to the week**, so the budget is
 * split across domains here and each goal receives only its own share. Attaching
 * one shared multi-domain commitment to every goal would make
 * `plannedSessionsPerWeek` count the whole week once per domain, and would make
 * the week checklist list cardio under strength.
 *
 * **A domain absent from the map is a real answer and callers must leave its
 * commitment unset** rather than substituting an empty one: a goal carrying
 * `{standard: [], short: [], minimumViable: []}` fails `validateDomainGoal`, and
 * one carrying invented items fails D-101.
 */
export type StarterCommitments = Readonly<Partial<Record<FitnessDomain, WeeklyCommitment>>>

export function buildStarterCommitments(input: StarterCommitmentInput): StarterCommitments {
  const budget = Math.floor(input.sessionsPerWeek)
  if (!Number.isFinite(budget) || budget < 1) return {}

  const plannable = SESSION_DOMAINS.filter(
    (domain) => input.domains.includes(domain) && isPlannedIntention(input.intentions[domain]),
  ).sort(
    (left, right) =>
      PRIORITY_ORDER[input.priorities[left] ?? 'none'] -
      PRIORITY_ORDER[input.priorities[right] ?? 'none'],
  )

  if (plannable.length === 0) return {}

  const shares = splitBudget(budget, plannable.length)
  const minutes = minutesOf(input.minutesPerSession)
  const commitments: Partial<Record<FitnessDomain, WeeklyCommitment>> = {}

  plannable.forEach((domain, index) => {
    /* A domain whose share came out at zero gets no commitment rather than a
       `timesPerWeek: 0` one — the model requires at least one, and "you said one
       session, so it goes to your primary goal" is the honest reading of a budget
       smaller than the number of domains. */
    const item = plannedItem(domain, shares[index] ?? 0, minutes)
    if (item === undefined) return
    /* Shared with the Gemini suggestion's pre-fill (D-093 §6) rather than
       duplicated, so the short week and the floor come from one rule whoever
       proposed the session count. */
    commitments[domain] = weekAround(item)
  })

  return commitments
}

/**
 * An equal share each, with the remainder handed out in priority order.
 *
 * **Integer division, not a ratio.** Four sessions across two domains is two and
 * two; five is three and two, and the extra one goes to whichever domain the user
 * called their primary goal. Nothing here is a number that would need approving.
 */
function splitBudget(budget: number, count: number): readonly number[] {
  const base = Math.floor(budget / count)
  const remainder = budget % count
  return Array.from({ length: count }, (_unused, index) => base + (index < remainder ? 1 : 0))
}

/** The typed session length, or nothing. A blank is never filled in (D-101 §3). */
function minutesOf(value: number | undefined): number | undefined {
  if (value === undefined || !Number.isFinite(value) || value <= 0) return undefined
  return Math.round(value)
}

function plannedItem(
  domain: FitnessDomain,
  timesPerWeek: number,
  minutes: number | undefined,
): PlannedItem | undefined {
  if (timesPerWeek < 1) return undefined
  /* The same label shape the week checklist already builds, so a planned item and
     its checklist row read as the same thing rather than as two features. */
  const label = `${FITNESS_DOMAIN_LABELS[domain]} sessions`
  return minutes === undefined ? { label, timesPerWeek } : { label, timesPerWeek, minutes }
}
